import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  EventMessage,
  EventType,
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser"
import { TokenClaims } from "@azure/msal-common"
import { b2cPolicies, msalConfig, GetAuthority } from "./authConfig"
import { store } from "../../../reduxStore/store"
import {
  authenticated,
  selectAccountGuid,
  unauthenticated,
  VeapAccountInfo,
  msalToVeapInfo,
} from "../authenticationSlice"
import { getUserRecord, userHandoff } from "../../../domain/Account/userSlice"
import appInsightsLogger from "../../logging/appInsightsLogger"

export const HEALTH_ADVOCATE_DIRECTORY_ISSUER =
  "https://login.microsoftonline.com/7e04e371-7683-42fb-ab0c-ec7d2155d044/v2.0"

class MsalClientSingleton {
  // Class
  private static instance: MsalClientSingleton

  public static getInstance(): MsalClientSingleton {
    if (!MsalClientSingleton.instance) {
      MsalClientSingleton.instance = new MsalClientSingleton()
    }
    return MsalClientSingleton.instance
  }

  // Instance
  private msalInstance: IPublicClientApplication

  private constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig)
  }

  public getMsalInstance(): IPublicClientApplication {
    return this.msalInstance
  }

  private withInteractionGuard = <T>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn: (...args: any[]) => Promise<T>,
    maxRetries: number = 10,
    retryDelay: number = 300,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async (...args: any[]): Promise<T> => {
      const attemptOperation = async (retryCount: number = 0): Promise<T> => {
        try {
          return await fn(...args)
        } catch (error) {
          if (
            error instanceof BrowserAuthError &&
            error.errorCode === "interaction_in_progress" &&
            retryCount < maxRetries
          ) {
            appInsightsLogger.traceInfo(
              `MSAL: Interaction in progress, retrying in ${retryDelay}ms (attempt ${retryCount + 1})`,
            )
            await new Promise((resolve) => setTimeout(resolve, retryDelay))
            return attemptOperation(retryCount + 1)
          }
          appInsightsLogger.traceError(`MSAL: operation error: ${error}`)
          throw error
        }
      }
      return attemptOperation()
    }
  }

  public refreshTokenSilently = this.withInteractionGuard(
    async (): Promise<AuthenticationResult | null> => {
      const activeAccount = this.msalInstance.getActiveAccount()
      if (!activeAccount) {
        throw new Error("No active account found")
      }
      const tokenExpirationTimestamp = activeAccount?.idTokenClaims?.exp
      if (!tokenExpirationTimestamp) {
        throw new Error("Token expiration timestamp not available")
      }
      const authority = this.getAuthorityForAccount(activeAccount)
      if (!authority) {
        throw new Error("No authority found for the active account")
      }

      const currentTime = Math.floor(Date.now() / 1000)
      const timeRemainingOnToken = tokenExpirationTimestamp - currentTime
      // Only refresh if token is close to expiration (4 minutes or less)
      if (timeRemainingOnToken > 240) {
        return null
      }

      const silentRequest = {
        scopes: ["openid"],
        account: activeAccount,
        authority: authority,
      }

      const response = await this.msalInstance.acquireTokenSilent(silentRequest)
      if (!response) {
        throw new Error("Failed to acquire new token")
      }
      return response
    },
  )

  private getAuthorityForAccount(account: AccountInfo): string | null {
    if (account && account.authorityType && account.tenantId) {
      const signUpSignIn = b2cPolicies.names.signUpSignIn
      const haSignUpSignIn = b2cPolicies.names.haSignUpSignIn
      const totpSignUpSignIn = b2cPolicies.names.totpSignUpSignIn
      const userSignUp = b2cPolicies.names.userSignUp

      const tenantIdToAuthorityMap = {
        [signUpSignIn]: b2cPolicies.authorities.signUpSignIn.authority,
        [haSignUpSignIn]: b2cPolicies.authorities.haSignUpSignIn.authority,
        [totpSignUpSignIn]: b2cPolicies.authorities.totpSignUpSignIn.authority,
        [userSignUp]: b2cPolicies.authorities.userSignUp.authority,
      }
      const authority = tenantIdToAuthorityMap[account.tenantId]

      if (!authority) {
        return null
      }

      return authority
    }
    return null
  }

  public async initialize(): Promise<void> {
    try {
      await this.msalInstance.initialize()

      this.handleRedirectLogin(this.getMsalInstance())

      this.msalInstance.addEventCallback(async (event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult
          const account: AccountInfo = payload.account
          // The id token is not set on the account initially, fetching it from the top level payload object
          if (!account.idToken && payload.idToken) {
            account.idToken = payload.idToken
          }
          this.msalInstance.setActiveAccount(account)
          // Set the token info in the authenticationSlice
          store.dispatch(authenticated(msalToVeapInfo(account)))
          // Get the newly set userId from the authenticationSlice
          const id = selectAccountGuid(store.getState())
          // Ping the database to get the user's data
          await store.dispatch(getUserRecord(id))
        }
      })

      // login success occurs before redirect end - this will ensure that the user can attempt to log in before
      // we try to check if the user has already logged in (preventing unauthentication flash)
      this.msalInstance.addEventCallback(async (event: EventMessage) => {
        if (event.eventType === EventType.HANDLE_REDIRECT_END) {
          const account = await this.msalInstance.getActiveAccount()
          if (account) {
            // User has refreshed/was already logged in.
            // Have to hit the DB again to get the user data.
            const veapAccount = msalToVeapInfo(account)
            store.dispatch(authenticated(veapAccount))
            store.dispatch(getUserRecord(veapAccount.localAccountId))
          } else {
            store.dispatch(unauthenticated())
          }
        }
      })

      this.msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_FAILURE) {
          store.dispatch(unauthenticated())
        }
      })

      this.msalInstance.addEventCallback(async (event: EventMessage) => {
        if (event.eventType === EventType.LOGOUT_FAILURE) {
          store.dispatch(unauthenticated())
        }
      })
    } catch (error) {
      appInsightsLogger.traceError(`MSAL: initialization error: ${error}`)
    }
  }

  public async handleRedirectLogin(
    msalInstance: IPublicClientApplication,
  ): Promise<void> {
    try {
      const loginResponse = await msalInstance.handleRedirectPromise()
      if (loginResponse !== null) {
        // handle HA login / MemPHIS handoff
        if (loginResponse.idTokenClaims) {
          const idToken = loginResponse.idTokenClaims as TokenClaims
          // confirm token issued by HA directory
          if (idToken.idp && idToken.idp == HEALTH_ADVOCATE_DIRECTORY_ISSUER) {
            // retrieve handoff data from state
            const handoffState = loginResponse.state ? loginResponse.state : ""
            console.info("handleRedirectLogin: HA Login Handoff:", handoffState)
            store.dispatch(userHandoff(handoffState))
          }
        }

        console.log("handleRedirectLogin: Login successful!")
      }
    } catch (error) {
      // handle redirect error
      appInsightsLogger.traceError(`MSAL: handleRedirectLogin error: ${error}`)
    }
  }

  public getExistingLogin = this.withInteractionGuard(
    async (): Promise<VeapAccountInfo | undefined> => {
      const activeAccount = this.msalInstance.getActiveAccount()
      if (!activeAccount) {
        return undefined
      }
      const idToken = await this.msalInstance.acquireTokenSilent({
        scopes: ["openid"],
        account: activeAccount ? activeAccount : undefined,
        authority: GetAuthority(activeAccount.tenantId),
      })
      if (idToken && activeAccount) {
        activeAccount.idToken = idToken.idToken
        return msalToVeapInfo(activeAccount)
      }
      return undefined
    },
  )

  public useExistingLogin(account: VeapAccountInfo): void {
    this.msalInstance.setActiveAccount(account)
  }

  public triggerSignUpRedirect = this.withInteractionGuard(
    async (): Promise<void> => {
      appInsightsLogger.traceInfo("MSAL: Affiliate SignUp")
      await this.msalInstance.loginRedirect({
        prompt: "login",
        authority: b2cPolicies.authorities.userSignUp.authority,
        scopes: ["openid", "profile", "email"],
      })
    },
  )

  public triggerLoginRedirect = this.withInteractionGuard(
    async (): Promise<void> => {
      appInsightsLogger.traceInfo("MSAL: Affiliate Login, Phone")
      await this.msalInstance.loginRedirect({
        prompt: "login",
        authority: b2cPolicies.authorities.signUpSignIn.authority,
        scopes: ["openid", "profile", "email"],
      })
    },
  )

  public triggerTotpLoginRedirect = this.withInteractionGuard(
    async (): Promise<void> => {
      appInsightsLogger.traceInfo("MSAL: Affiliate Login, App")
      await this.msalInstance.loginRedirect({
        prompt: "login",
        authority: b2cPolicies.authorities.totpSignUpSignIn.authority,
        scopes: ["openid", "profile", "email"],
      })
    },
  )

  public triggerHaLoginRedirect = this.withInteractionGuard(
    async (caseId: string): Promise<void> => {
      appInsightsLogger.traceInfo(`MSAL: HA Login; caseId: ${caseId}`)
      await this.msalInstance.loginRedirect({
        authority: b2cPolicies.authorities.haSignUpSignIn.authority,
        scopes: ["openid", "profile", "email"],
        state: caseId,
      })
    },
  )

  public triggerLogout = this.withInteractionGuard(async (): Promise<void> => {
    store.dispatch(userHandoff(""))
    const activeAccount = this.msalInstance.getActiveAccount()
    await this.msalInstance.logoutRedirect({
      account: activeAccount || undefined,
    })
    appInsightsLogger.traceInfo("MSAL: logout success")
  })
}

export default MsalClientSingleton.getInstance()
